import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api, API } from '../lib/api';
import { useApp } from '../context/AppContext';
import { useT } from '../lib/i18n';
import { useHomeLang } from '../lib/useHomeLang';
import { Play, PlusCircle, Star, Flame, Sparkles, Bot as BotIcon, X, LifeBuoy, ChevronRight, Languages } from 'lucide-react';
import PinGateModal from '../components/PinGateModal';
import { FeaturedRewardCard } from '../components/FeaturedRewardCard';
import { HomeStreaksLine } from '../components/StreaksExplainer';
import { stripMarkers } from '../lib/textMarkers';
import StarterLibrarySection from '../components/StarterLibrarySection';

export default function Home() {
  const nav = useNavigate();
  const { activeChild, uiLang } = useApp();
  const t = useT(uiLang);
  // Iter-52.17 — Home-only Hindi/English toggle. Reads the parent's
  // saved preference from localStorage (`rb_home_lang`), defaults to
  // 'en' on first load. Scope is Home page ONLY per spec; other tabs
  // stay English. `h(key)` looks up Home-scoped strings, `hint()`
  // returns the dual-hint copy (always in the OTHER language).
  const { lang: homeLang, toggle: toggleHomeLang, t: h, hint } = useHomeLang();
  const isHi = homeLang === 'hi';
  const [dash, setDash] = useState(null);
  const [books, setBooks] = useState([]);
  const [gateOpen, setGateOpen] = useState(false);
  const [showBotPicker, setShowBotPicker] = useState(false);

  function launchQuiz(book) {
    if (!book) return;
    const currentIdx = book.current_page_index || 0;
    const page = book.pages?.[currentIdx];
    if (!page) return;
    const text = stripMarkers(page.text || '').trim();
    nav(`/quiz/${book.book_id}`, {
      state: {
        text,
        language: book.language || 'en',
        cacheKey: `${book.book_id}:page:${page.page_id}`,
        pageIdx: currentIdx,
        chunkIdx: 0,
        pages: book.pages,
        currentChunkText: text,
        bookTitle: book.title || '',
      },
    });
  }
  function onHomeHeroTap() {
    if (!books || books.length === 0) return;
    if (books.length === 1) { launchQuiz(books[0]); return; }
    setShowBotPicker(true);
  }

  useEffect(() => {
    if (!activeChild) return;
    api.get(`/children/${activeChild.child_id}/dashboard`).then(({data}) => setDash(data));
    api.get(`/children/${activeChild.child_id}/books`).then(({data}) => setBooks(data));
    // Play warm scripted greeting once per visit
    const key = `greeted-${activeChild.child_id}-${new Date().toDateString()}`;
    if (!sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, '1');
      api.post('/lessons/greeting', { child_id: activeChild.child_id }).then(async ({data}) => {
        try {
          const resp = await fetch(`${API}/tts`, { method: 'POST', credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ text: data.text, language: data.language, speed: activeChild.tts_speed ?? 0.75, voice_gender: activeChild.voice_gender || 'female' }) });
          if (resp.ok) new Audio(URL.createObjectURL(await resp.blob())).play().catch(()=>{});
        } catch {}
      }).catch(()=>{});
    }
  }, [activeChild]);

  if (!activeChild) return null;
  const currentBook = books[0];
  const nextPage = currentBook?.pages?.[currentBook?.current_page_index || 0];

  return (
    <div className="px-6 pt-6 pb-36 max-w-5xl mx-auto overflow-x-hidden">
      {/* Iter-52.17 — Home language toggle. Sits ABOVE the greeting,
          right-aligned, with a dual-hint. The hint text is always in
          the OTHER language, so a parent who can't read the current
          UI language still sees an instruction they can follow to
          switch. Default state is English (per spec); tapping the
          pill flips + persists to localStorage. */}
      <div
        data-testid="home-lang-toggle-row"
        className="flex items-center justify-end gap-3 mb-3 flex-wrap"
      >
        <span
          data-testid="home-lang-toggle-hint"
          className={`text-xs font-bold text-[#2B2140]/70 leading-snug max-w-[220px] text-right ${isHi ? '' : 'font-hi'}`}
        >
          {hint()}
        </span>
        <button
          data-testid="home-lang-toggle"
          onClick={toggleHomeLang}
          aria-label={isHi ? 'Switch to English' : 'हिंदी में देखें'}
          className="inline-flex items-center gap-1 bg-white border-2 border-[#4C3B99] rounded-full p-1 shadow-sm"
        >
          <Languages size={14} strokeWidth={2.5} className="ml-2 text-[#4C3B99]"/>
          <span
            className={`px-3 py-1 rounded-full text-xs font-extrabold transition-colors ${
              !isHi ? 'bg-[#4C3B99] text-white' : 'text-[#4C3B99]'
            }`}
          >EN</span>
          <span
            className={`px-3 py-1 rounded-full text-xs font-extrabold font-hi transition-colors ${
              isHi ? 'bg-[#4C3B99] text-white' : 'text-[#4C3B99]'
            }`}
          >हिं</span>
        </button>
      </div>

      <div className="flex justify-between items-start mb-6 gap-3 flex-wrap">
        <div className="min-w-0">
          <p className={`text-sm font-bold text-[#2B2140]/60 uppercase tracking-widest ${isHi ? 'font-hi' : ''}`}>{h('hi_greeting')}</p>
          <h1 className="text-3xl md:text-4xl font-extrabold break-words">{activeChild.name} 👋</h1>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <div data-testid="total-stars" className="bg-[#FF7A45] rounded-2xl px-4 py-2 flex items-center gap-2 text-white">
            <Star size={20} strokeWidth={2.5} className="text-white fill-white"/>
            <div>
              <div className={`text-[10px] font-bold text-white/80 ${isHi ? 'font-hi' : ''}`}>{h('total_stars_label')}</div>
              <div className="text-lg font-extrabold">{dash?.total_points ?? 0}</div>
            </div>
          </div>
          <div data-testid="streak" className="bg-[#4A9B7F] rounded-2xl px-4 py-2 flex items-center gap-2 text-white">
            <Flame size={20} strokeWidth={2.5} className="text-white"/>
            <div>
              <div className={`text-[10px] font-bold text-white/80 ${isHi ? 'font-hi' : ''}`}>{h('streak_label')}</div>
              <div className="text-lg font-extrabold">{dash?.streak ?? 0}</div>
            </div>
          </div>
        </div>
      </div>

      <HomeStreaksLine lang={homeLang}/>

      {/* Iter-52.8 — Permanent Guides & Info reminder. Explicitly NOT
          tied to any "seen once" state (no sessionStorage / no
          user.guides_seen flag) so a returning parent who forgets
          where help lives always sees the pointer.
          Iter-52.16 — repainted from a washed-out white card to a
          solid indigo tile so it stands out against the cream
          background and hits WCAG AA contrast for the white body copy. */}
      <Link
        data-testid="home-guides-reminder"
        to="/how-to-use"
        className="w-full mb-6 flex items-center gap-3 rounded-2xl p-4 text-white transition-transform hover:scale-[1.01] active:scale-[0.99]"
        style={{
          background: 'linear-gradient(135deg, #4C3B99 0%, #6A4FCF 100%)',
          boxShadow: '0 12px 30px -14px rgba(76,59,153,0.55)',
        }}
      >
        <div className="w-11 h-11 rounded-2xl bg-white/25 text-white flex items-center justify-center shrink-0">
          <LifeBuoy size={22} strokeWidth={2.5}/>
        </div>
        <div className="flex-1 min-w-0">
          <div className={`font-extrabold text-white leading-snug text-base ${isHi ? 'font-hi' : ''}`}>
            {h('guides_title')}
          </div>
          <div className={`text-xs text-white/90 mt-0.5 leading-snug font-bold ${isHi ? 'font-hi' : ''}`}>
            {h('guides_subtitle')}
          </div>
        </div>
        <ChevronRight size={20} strokeWidth={3} className="text-white shrink-0"/>
      </Link>

      {/* Iter-52.16 — Starter Library preview moved ABOVE Beat the Bot
          so new users see the entry point instead of scrolling past
          Beat-the-Bot / Continue / Quick / All-books to find it.
          Compact preview only — full grid lives at /library.
          Iter-52.17 — forwards `homeLang` so the card translates in
          lockstep with the toggle. */}
      <StarterLibrarySection childId={activeChild?.child_id} homeLang={homeLang}/>

      {/* Iter-51.28 — Beat the Bot hero card. Hidden entirely when
          the child has no books yet (falls back to the add-book CTA
          below). Tap opens a book chooser sheet; if only one book
          exists we short-circuit and launch straight into the quiz.
          Iter-51.39 — recoloured to teal so it doesn't stack visually
          with the orange Total-stars stat chip above it. */}
      {books && books.length > 0 && (
        <button
          data-testid="home-btb-hero"
          onClick={onHomeHeroTap}
          className="w-full mb-6 rounded-3xl p-6 text-left relative overflow-hidden transition-transform hover:scale-[1.01] active:scale-[0.99]"
          style={{
            background: 'linear-gradient(135deg, #0D9488 0%, #14B8A6 100%)',
            boxShadow: '0 12px 30px -12px rgba(13,148,136,0.55)',
          }}
        >
          {/* Subtle animated pulse ring on the right — draws the eye */}
          <span aria-hidden="true"
            className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-white/20 animate-pulse"/>
          <span aria-hidden="true"
            className="absolute right-6 top-6 w-16 h-16 rounded-full bg-white/25 flex items-center justify-center">
            <BotIcon size={32} strokeWidth={2.5} className="text-white"/>
          </span>
          <div className="relative z-10 pr-24">
            <div className={`text-xs font-extrabold uppercase tracking-widest text-white/85 mb-1 ${isHi ? 'font-hi' : ''}`}>{h('btb_eyebrow')}</div>
            <h3 className={`text-2xl font-extrabold text-white leading-tight ${isHi ? 'font-hi' : ''}`}>{h('btb_title')}</h3>
            <p className={`text-sm font-bold text-white/90 mt-1 ${isHi ? 'font-hi' : ''}`}>
              {h('btb_subtitle')}
            </p>
          </div>
        </button>
      )}

      {dash?.featured_reward && (
        <FeaturedRewardCard reward={dash.featured_reward} totalPoints={dash.total_points}/>
      )}

      {currentBook && nextPage ? (
        <div className="bg-[#5B8DEF] text-white rounded-3xl p-8 mb-6 relative overflow-hidden">
          <div className={`absolute top-4 right-4 bg-white/25 rounded-full px-3 py-1 text-xs font-bold text-white ${isHi ? 'font-hi' : ''}`}>{h('continue_reading')}</div>
          <h3 className="text-sm font-bold text-white/80 uppercase tracking-widest mb-2">{currentBook.title}</h3>
          <p className={`text-2xl font-bold mb-6 line-clamp-3 ${currentBook.language==='hi'?'font-hi':''}`}>
            {stripMarkers(nextPage.text || '').slice(0, 160) || '…'}
          </p>
          <button data-testid="start-reading-btn" onClick={() => nav(`/book/${currentBook.book_id}`)}
            className={`pushable pushable-primary bg-[#FF6B1A] text-white font-bold px-8 py-4 rounded-full inline-flex items-center gap-2 text-lg ${isHi ? 'font-hi' : ''}`}>
            <Play size={22} strokeWidth={3} className="fill-white"/> {h('start_reading')}
          </button>
        </div>
      ) : (
        <div className="bg-[#F4B740] rounded-3xl p-8 mb-6 text-center">
          <Sparkles size={48} strokeWidth={2.5} className="text-[#FF6B1A] mx-auto mb-3"/>
          <p className={`text-xl font-bold mb-4 ${isHi ? 'font-hi' : ''}`}>{h('no_books')}</p>
          <button data-testid="add-first-book-btn" onClick={() => setGateOpen('addbook')}
            className={`pushable pushable-primary bg-[#FF6B1A] text-white font-bold px-8 py-4 rounded-full inline-flex items-center gap-2 ${isHi ? 'font-hi' : ''}`}>
            <PlusCircle size={22} strokeWidth={3}/> {h('add_book')}
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <button data-testid="quick-practice-btn" onClick={() => setGateOpen('quick')} className="bg-[#F4B740] rounded-3xl p-6 text-left hover:scale-[1.02] transition-transform">
          <Sparkles size={32} strokeWidth={2.5} className="text-[#FF6B1A] mb-2"/>
          <div className={`font-extrabold text-xl text-[#2B2140] ${isHi ? 'font-hi' : ''}`}>{h('quick_practice')}</div>
          <div className={`text-sm text-[#2B2140]/70 ${isHi ? 'font-hi' : ''}`}>{h('quick_practice_sub')}</div>
        </button>
        <button data-testid="all-books-btn" onClick={() => nav('/books')} className="bg-[#D97398] rounded-3xl p-6 text-left hover:scale-[1.02] transition-transform text-white">
          <PlusCircle size={32} strokeWidth={2.5} className="text-white mb-2"/>
          <div className={`font-extrabold text-xl ${isHi ? 'font-hi' : ''}`}>{h('all_my_books')}</div>
          <div className={`text-sm text-white/80 ${isHi ? 'font-hi' : ''}`}>{h('all_my_books_sub')}</div>
        </button>
      </div>

      {/* Iter-52.16 — Starter Library preview moved to the TOP of
          Home (see the block above the Beat-the-Bot hero). This spot
          previously rendered the compact preview but new users never
          scrolled this far, so the section was invisible in practice. */}

      {gateOpen && (
        <PinGateModal
          onUnlocked={() => { const target = gateOpen === 'quick' ? '/quick' : '/books/new'; setGateOpen(false); nav(target); }}
          onCancel={() => setGateOpen(false)}
        />
      )}

      {/* Iter-51.28 — Beat the Bot book chooser sheet */}
      {showBotPicker && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-3"
          data-testid="home-btb-picker-modal"
          onClick={() => setShowBotPicker(false)}>
          <div
            className="bg-white rounded-3xl w-full max-w-md p-4 shadow-2xl"
            onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3 px-1">
              <div>
                <h3 className={`text-lg font-extrabold text-[#2B2140] ${isHi ? 'font-hi' : ''}`}>{h('picker_title')}</h3>
                <p className={`text-xs font-bold text-slate-500 ${isHi ? 'font-hi' : ''}`}>{h('picker_subtitle')}</p>
              </div>
              <button data-testid="home-btb-picker-close"
                onClick={() => setShowBotPicker(false)}
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center flex-shrink-0">
                <X size={16} strokeWidth={3}/>
              </button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto space-y-2 pr-1">
              {books.map(b => {
                const cover = b.pages?.[0]?.image_url;
                return (
                  <button
                    key={b.book_id}
                    data-testid={`home-btb-pick-${b.book_id}`}
                    onClick={() => { setShowBotPicker(false); launchQuiz(b); }}
                    className="w-full text-left flex items-center gap-3 p-3 rounded-2xl bg-slate-50 border-2 border-slate-100 hover:border-[#FF6B1A] transition-colors"
                  >
                    <div className="w-12 h-12 rounded-xl bg-white border-2 border-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                      {cover ? (
                        <img src={cover} alt="" className="w-full h-full object-cover"/>
                      ) : (
                        <BotIcon size={22} className="text-slate-400"/>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-extrabold text-[#2B2140] truncate">{b.title || h('picker_untitled')}</div>
                      <div className={`text-xs font-bold text-slate-500 truncate ${isHi ? 'font-hi' : ''}`}>
                        {b.language === 'hi' ? 'Hindi' : 'English'} · {b.pages?.length || 0} {h('picker_pages_suffix')}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
